import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Hash, ChatCircleDots, Lock, MagnifyingGlass } from '@phosphor-icons/react';
import { canManageChat } from '@/auth/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewChannelDialog({ open, onOpenChange, directory, currentUser, onCreated, onStartDm }) {
  const canCreateChannel = canManageChat(currentUser);
  const [tab, setTab] = useState(canCreateChannel ? 'channel' : 'dm');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dmSearch, setDmSearch] = useState('');

  const reset = () => { setName(''); setDescription(''); setTags(''); setIsPrivate(false); setDmSearch(''); };

  const createChannel = async () => {
    if (!name.trim()) { toast.error('チャンネル名を入力してください'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/chat/channels`, {
        name: name.trim(),
        description: description.trim(),
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
        is_private: isPrivate,
      });
      toast.success('チャンネルを作成しました');
      reset();
      onOpenChange(false);
      onCreated(res.data);
    } catch (_e) {
      toast.error('作成に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const dmUsers = directory.filter((u) => u.id !== currentUser?.id &&
    (u.name || u.email || '').toLowerCase().includes(dmSearch.toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="new-channel-dialog">
        <DialogHeader>
          <DialogTitle>新しい会話</DialogTitle>
          <DialogDescription className="sr-only">新しいチャンネルを作成するか、ダイレクトメッセージを開始します。</DialogDescription>
        </DialogHeader>

        <div className="inline-flex items-center bg-zinc-100 rounded-lg p-0.5 mb-3">
          {canCreateChannel && (
            <button type="button" onClick={() => setTab('channel')} data-testid="new-tab-channel"
              className={`px-3 py-1.5 text-sm font-medium rounded-md inline-flex items-center gap-1.5 ${tab === 'channel' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500'}`}>
              <Hash size={15} /> チャンネル
            </button>
          )}
          <button type="button" onClick={() => setTab('dm')} data-testid="new-tab-dm"
            className={`px-3 py-1.5 text-sm font-medium rounded-md inline-flex items-center gap-1.5 ${tab === 'dm' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500'}`}>
            <ChatCircleDots size={15} /> ダイレクト
          </button>
        </div>

        {tab === 'channel' ? (
          <div className="space-y-3">
            <Input placeholder="チャンネル名（例: 全社アナウンス）" value={name} onChange={(e) => setName(e.target.value)} data-testid="channel-name-input" />
            <Input placeholder="説明（任意）" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="channel-desc-input" />
            <Input placeholder="タグ（カンマ区切り 例: 総務, お知らせ）" value={tags} onChange={(e) => setTags(e.target.value)} data-testid="channel-tags-input" />
            <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer">
              <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} data-testid="channel-private-checkbox" />
              <Lock size={14} /> 非公開チャンネル（招待されたメンバーのみ）
            </label>
            <Button onClick={createChannel} disabled={saving} data-testid="create-channel-btn" className="w-full bg-teal-700 hover:bg-teal-800">
              {saving ? '作成中...' : 'チャンネルを作成'}
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="relative">
              <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input placeholder="名前で検索..." value={dmSearch} onChange={(e) => setDmSearch(e.target.value)} className="pl-9" data-testid="dm-search-input" />
            </div>
            <div className="max-h-64 overflow-y-auto -mx-1">
              {dmUsers.map((u) => (
                <button key={u.id} type="button" onClick={() => { onOpenChange(false); reset(); onStartDm(u.id); }}
                  data-testid={`dm-user-${u.id}`}
                  className="w-full text-left flex items-center gap-2.5 px-2 py-2 rounded-md hover:bg-zinc-50">
                  <span className="w-8 h-8 rounded-full bg-zinc-100 text-zinc-700 text-xs font-bold flex items-center justify-center shrink-0">
                    {(u.name || u.email || '?').charAt(0).toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-zinc-900 truncate">{u.name || u.email}</p>
                    <p className="text-[11px] text-zinc-400 truncate">{u.email}</p>
                  </div>
                </button>
              ))}
              {dmUsers.length === 0 && <p className="text-sm text-zinc-400 text-center py-6">ユーザーが見つかりません</p>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
